import React, { useState, useEffect } from 'react';
import { authFetch } from '../auth/AuthContext';

const ATTENTION = {
  'New Inquiry': { tone: '#EF9F27', reason: 'New inquiry, needs review' },
  'Terms Received': { tone: '#1A5FA8', reason: 'Terms received, ready to review' },
};

const PIPELINE = [
  { label: 'New Inquiry', stages: ['New Inquiry'], color: '#154D97' },
  { label: 'Application', stages: ['Application'], color: '#4A7FBF' },
  { label: 'Review', stages: ['Review'], color: '#7EA3D7' },
  { label: 'Sent to lenders', stages: ['Sent to lenders'], color: '#A9C4E6' },
  { label: 'Terms Received', stages: ['Terms Received'], color: '#C9D9EE' },
  { label: 'Processing', stages: ['Processing'], color: '#E4EDF8' },
];

const AMOUNT_FIELDS = ['Loan amount', 'Loan Amount', 'Requested loan amount', 'Loan amount requested', 'Loan request', 'Loan request amount', 'Loan size', 'Amount'];

function clean(str) {
  return String(str || '').replace(/\s*[\u2014\u2013\u2015\u2012\u2212]\s*/g, ' - ');
}

function stageDot(st) {
  if (st === 'Terms Received' || st === 'Funded') return '#3B6D11';
  if (st === 'Closed Lost') return '#C9D9EE';
  return '#4A7FBF';
}

function loanAmount(fields) {
  if (!fields) return 0;
  for (const f of AMOUNT_FIELDS) {
    const v = fields[f];
    if (typeof v === 'number') return v;
    if (typeof v === 'string') {
      const n = Number(v.replace(/[^0-9.]/g, ''));
      if (n > 0) return n;
    }
  }
  return 0;
}

function formatMoney(n) {
  if (n >= 1000000) return '$' + (n / 1000000).toFixed(1) + 'M';
  if (n >= 1000) return '$' + Math.round(n / 1000) + 'K';
  return '$' + Math.round(n).toLocaleString();
}

function relativeDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (isNaN(d)) return '';
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.floor((startOfToday - new Date(d.getFullYear(), d.getMonth(), d.getDate())) / 86400000);
  if (diffDays <= 0) return 'Today';
  if (diffDays === 1) return 'Yesterday';
  if (diffDays < 7) return diffDays + ' days ago';
  return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

const POP = "'Inter',sans-serif";

const s = {
  greet: { fontFamily: POP, fontSize: '21px', fontWeight: 600, letterSpacing: '-0.005em', color: '#2E4763', margin: 0 },
  greetSub: { fontSize: '13px', color: '#A7B3C4', marginTop: '2px', marginBottom: '16px' },

  grid: { display: 'grid', gap: '14px', gridTemplateColumns: 'repeat(4, 1fr)' },
  box: { background: '#fff', border: '1px solid #C9D9EE', borderRadius: '12px', padding: '18px 20px', boxShadow: '0 1px 3px rgba(20,60,120,0.06)' },
  boxLbl: { fontFamily: POP, fontSize: '10px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#154D97', marginBottom: '12px', display: 'flex', alignItems: 'center', justifyContent: 'space-between' },
  boxLblR: { fontSize: '10px', fontWeight: 600, color: '#7C97AC', letterSpacing: 0, textTransform: 'none' },

  kbox: { display: 'flex', flexDirection: 'column', justifyContent: 'center' },
  kpiCard: { background: '#fff', border: '1px solid #C9D9EE', borderRadius: '12px', boxShadow: '0 1px 3px rgba(20,60,120,0.06)', overflow: 'hidden' },
  kpiBar: { background: '#D0E0F5', borderBottom: '1px solid #BCD2EC', minHeight: '32px', display: 'flex', alignItems: 'center', padding: '0 14px' },
  kpiBarLbl: { fontFamily: POP, fontSize: '9.5px', fontWeight: 700, letterSpacing: '0.06em', textTransform: 'uppercase', color: '#154D97' },
  kpiBody: { padding: '13px 15px' },
  kpiK: { fontSize: '11px', color: '#A7B3C4', fontWeight: 500 },
  kpiV: (color) => ({ fontSize: '26px', fontWeight: 600, color, letterSpacing: '-0.01em' }),
  kpiTrend: { fontSize: '11px', color: '#7C97AC', marginTop: '4px' },

  leftStack: { gridColumn: 'span 2', display: 'flex', flexDirection: 'column' },
  rightStack: { gridColumn: 'span 2', display: 'flex', flexDirection: 'column', gap: '14px' },
  grow: { flex: 1 },
  span4: { gridColumn: 'span 4' },

  donutWrap: { display: 'flex', alignItems: 'center', gap: '20px' },
  legend: { fontSize: '12px', color: '#5a6b82', display: 'flex', flexDirection: 'column', gap: '7px', flex: 1 },
  legRow: { display: 'flex', alignItems: 'center', gap: '8px' },
  legDot: (c) => ({ width: '9px', height: '9px', borderRadius: '3px', flexShrink: 0, background: c }),
  legNum: (zero) => ({ marginLeft: 'auto', fontWeight: zero ? 500 : 600, color: zero ? '#CBD3DE' : '#2E4763' }),

  trendTop: { display: 'flex', alignItems: 'baseline', gap: '10px', marginBottom: '8px' },
  trendBig: { fontSize: '22px', fontWeight: 600, color: '#154D97' },
  trendDelta: { fontSize: '11.5px', fontWeight: 600, color: '#154D97', background: '#E4EDF8', borderRadius: '10px', padding: '2px 9px' },
  trendNote: { fontSize: '12px', color: '#A7B3C4', marginTop: '10px' },

  chip: { display: 'inline-block', fontSize: '10px', fontWeight: 600, color: '#154D97', background: '#E4EDF8', borderRadius: '20px', padding: '2px 10px' },
  dealName: { fontSize: '13px', fontWeight: 600, color: '#2E4763', marginTop: '7px' },
  aAmt: { fontSize: '13px', fontWeight: 600, color: '#154D97' },
  muted: { fontSize: '12.5px', color: '#A7B3C4' },

  actItem: (last) => ({ display: 'flex', gap: '10px', alignItems: 'flex-start', padding: '9px 0', borderBottom: last ? 'none' : '1px solid #F2F5F9', cursor: 'pointer' }),
  actDot: (c) => ({ width: '7px', height: '7px', borderRadius: '50%', marginTop: '5px', flexShrink: 0, background: c }),
  actText: { fontSize: '12.5px', color: '#5a6b82' },
  actTime: { fontSize: '11px', color: '#C0CAD8', marginTop: '1px' },
  needRow: (last) => ({ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 0', borderBottom: last ? 'none' : '1px solid #F2F5F9', cursor: 'pointer' }),
  needMid: { flex: 1, minWidth: 0 },
  needTitle: { fontSize: '13px', fontWeight: 500, color: '#2E4763' },
  needSub: { fontSize: '11.5px', color: '#A7B3C4', marginTop: '1px' },
  chev: { fontSize: '15px', color: '#C7D2DF' },
};

function Donut({ counts, palette, center }) {
  const total = counts.reduce((a, b) => a + b, 0);
  let acc = 0;
  const segs = counts.map((n, i) => {
    const pct = total ? (n / total) * 100 : 0;
    const seg = { color: palette[i], dash: `${pct} ${100 - pct}`, offset: 25 - acc, show: n > 0 };
    acc += pct;
    return seg;
  });
  return (
    <svg width="120" height="120" viewBox="0 0 42 42">
      <circle cx="21" cy="21" r="15.9" fill="none" stroke="#E4EDF8" strokeWidth="5.5" />
      {total > 0 && segs.filter(x => x.show).map((x, i) => (
        <circle key={i} cx="21" cy="21" r="15.9" fill="none" stroke={x.color} strokeWidth="5.5"
          strokeDasharray={x.dash} strokeDashoffset={x.offset} />
      ))}
      <text x="21" y="20" textAnchor="middle" fontSize="7" fontWeight="700" fill="#2E4763">{center}</text>
      <text x="21" y="26" textAnchor="middle" fontSize="2.8" fill="#A7B3C4">active</text>
    </svg>
  );
}

function TrendChart({ values }) {
  const n = values.length;
  const max = Math.max(1, ...values);
  const W = 660, H = 104, pad = 10;
  const pts = values.map((v, i) => {
    const x = n === 1 ? W : (i / (n - 1)) * W;
    const y = H - pad - (v / max) * (H - pad * 2);
    return [x, y];
  });
  const line = pts.map((p, i) => (i === 0 ? 'M' : 'L') + p[0].toFixed(1) + ',' + p[1].toFixed(1)).join(' ');
  const area = line + ` L${W},${H} L0,${H} Z`;
  const last = pts[pts.length - 1];
  return (
    <svg width="100%" height="104" viewBox={`0 0 ${W} ${H}`} preserveAspectRatio="none" style={{ display: 'block' }}>
      <defs>
        <linearGradient id="trendFill" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#BCD2EC" stopOpacity="0.45" />
          <stop offset="1" stopColor="#BCD2EC" stopOpacity="0" />
        </linearGradient>
      </defs>
      <path d={area} fill="url(#trendFill)" />
      <path d={line} fill="none" stroke="#4A7FBF" strokeWidth="2.5" />
      <circle cx={last[0]} cy={last[1]} r="3.5" fill="#4A7FBF" />
    </svg>
  );
}

export default function Dashboard({ onNavigate }) {
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const d = await authFetch('/.netlify/functions/get-deals').then(r => r.json());
        setDeals(d.records || []);
      } catch (e) {
        console.error(e);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const hour = new Date().getHours();
  const greeting = hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const today = new Date().toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric' });

  const active = deals.filter(d => !['Funded', 'Closed Lost'].includes(d.fields?.Stage));
  const activeCount = active.length;
  const termsReceived = deals.filter(d => d.fields?.Stage === 'Terms Received').length;
  const newInquiries = deals.filter(d => d.fields?.Stage === 'New Inquiry').length;
  const volume = active.reduce((sum, d) => sum + loanAmount(d.fields), 0);

  const pipeCounts = PIPELINE.map(p => deals.filter(d => p.stages.includes(d.fields?.Stage)).length);
  const palette = PIPELINE.map(p => p.color);

  const needsYou = deals.filter(d => ATTENTION[d.fields?.Stage]).slice(0, 6);

  const dated = deals.filter(d => d.createdTime).sort((a, b) => new Date(b.createdTime) - new Date(a.createdTime));
  const latest = dated[0] || deals[0] || null;

  // Volume trend: monthly new-deal volume over last 6 months, from createdTime.
  const now = new Date();
  const months = [];
  for (let i = 5; i >= 0; i--) {
    const dt = new Date(now.getFullYear(), now.getMonth() - i, 1);
    months.push({ y: dt.getFullYear(), m: dt.getMonth(), total: 0 });
  }
  deals.forEach(d => {
    if (!d.createdTime) return;
    const ct = new Date(d.createdTime);
    const b = months.find(mm => mm.y === ct.getFullYear() && mm.m === ct.getMonth());
    if (b) b.total += loanAmount(d.fields);
  });
  const trendVals = months.map(mm => mm.total);
  const trendHasData = trendVals.filter(v => v > 0).length >= 2;

  const activity = dated.slice(0, 5);

  const tiles = [
    { label: 'Active deals', value: activeCount, color: '#2E4763', sub: activeCount ? 'live now' : 'none active' },
    { label: 'Active volume', value: volume > 0 ? formatMoney(volume) : '$0', color: '#2E4763', sub: activeCount ? 'across ' + activeCount + ' deal' + (activeCount === 1 ? '' : 's') : '' },
    { label: 'Terms received', value: termsReceived, color: '#1A5FA8', sub: 'awaiting lenders' },
    { label: 'New inquiries', value: newInquiries, color: '#8a5a12', sub: 'this week' },
  ];

  return (
    <div>
      <div style={s.greet}>{greeting}, Jennifer</div>
      <div style={s.greetSub}>{today}</div>

      <div style={s.grid}>
        {tiles.map((t, i) => (
          <div key={i} style={s.kpiCard}>
            <div style={s.kpiBar}><span style={s.kpiBarLbl}>{t.label}</span></div>
            <div style={s.kpiBody}>
              <div style={s.kpiV(t.color)}>{loading ? '·' : t.value}</div>
              {t.sub && <div style={s.kpiTrend}>{t.sub}</div>}
            </div>
          </div>
        ))}

        <div style={s.leftStack}>
          <div style={{ ...s.box, ...s.grow }}>
            <div style={s.boxLbl}>Pipeline by stage</div>
            <div style={s.donutWrap}>
              <Donut counts={pipeCounts} palette={palette} center={loading ? '·' : activeCount} />
              <div style={s.legend}>
                {PIPELINE.map((p, i) => (
                  <div key={p.label} style={s.legRow}>
                    <span style={s.legDot(p.color)} />
                    {p.label}
                    <span style={s.legNum(pipeCounts[i] === 0)}>{pipeCounts[i]}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <div style={s.rightStack}>
          <div style={{ ...s.box, ...s.grow }}>
            <div style={s.boxLbl}>Needs you</div>
            {loading ? <div style={s.muted}>Loading</div>
              : needsYou.length === 0 ? <div style={s.muted}>Nothing needs you right now.</div>
              : needsYou.map((d, i) => {
                  const a = ATTENTION[d.fields?.Stage];
                  return (
                    <div key={i} style={s.needRow(i === needsYou.length - 1)} onClick={() => onNavigate('deals')}>
                      <span style={s.actDot(a.tone)} />
                      <div style={s.needMid}>
                        <div style={s.needTitle}>{clean(d.fields?.['Deal name']) || 'Unnamed deal'}</div>
                        <div style={s.needSub}>{a.reason}</div>
                      </div>
                      <i className="ti ti-chevron-right" style={s.chev} />
                    </div>
                  );
                })}
          </div>
          <div style={{ ...s.box, ...s.grow }}>
            <div style={s.boxLbl}>Latest deal</div>
            {loading ? <div style={s.muted}>Loading</div>
              : !latest ? <div style={s.muted}>No deals yet.</div>
              : (
                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', cursor: 'pointer' }} onClick={() => onNavigate('deals')}>
                  <div>
                    <span style={s.chip}>{clean(latest.fields?.Stage) || 'No stage'}</span>
                    <div style={s.dealName}>{clean(latest.fields?.['Deal name']) || 'Unnamed deal'}</div>
                  </div>
                  {loanAmount(latest.fields) > 0 && <div style={s.aAmt}>{formatMoney(loanAmount(latest.fields))}</div>}
                </div>
              )}
          </div>
        </div>

        <div style={{ ...s.box, ...s.span4 }}>
          <div style={s.boxLbl}>Volume trend<span style={s.boxLblR}>Last 6 months</span></div>
          <div style={s.trendTop}>
            <span style={s.trendBig}>{loading ? '·' : (volume > 0 ? formatMoney(volume) : '$0')}</span>
            {activeCount > 0 && <span style={s.trendDelta}>active</span>}
          </div>
          {loading ? <div style={s.muted}>Loading</div>
            : trendHasData ? <TrendChart values={trendVals} />
            : <div style={s.trendNote}>Your volume trend fills in here as you add and move deals over the coming months.</div>}
        </div>

        <div style={{ ...s.box, ...s.span4 }}>
          <div style={s.boxLbl}>Recent activity</div>
          {loading ? <div style={s.muted}>Loading</div>
            : activity.length === 0 ? <div style={s.muted}>Activity will appear here as you create and work deals.</div>
            : activity.map((d, i) => (
                <div key={d.id} style={s.actItem(i === activity.length - 1)} onClick={() => onNavigate('deals')}>
                  <span style={s.actDot(stageDot(d.fields?.Stage))} />
                  <div>
                    <div style={s.actText}>New deal: {clean(d.fields?.['Deal name']) || 'Unnamed deal'}</div>
                    <div style={s.actTime}>{relativeDate(d.createdTime)}</div>
                  </div>
                </div>
              ))}
        </div>
      </div>
    </div>
  );
}
